import { useState } from 'react'
import { BookOpen, Loader2, Luggage, Plus, RefreshCw } from 'lucide-react'

import { Field } from '../../components/Field'
import { useTrip } from '../../data/tripContext'
import { fehlerSatz, rolleName } from '../../data/trips'

/**
 * Die Auswahl der Reise, bevor die App aufgeht.
 *
 * Sie erscheint nur, wenn es wirklich etwas zu entscheiden gibt: bei mehreren
 * Reisen ohne gemerkte Wahl, wenn noch gar keine da ist, oder wenn die offene
 * Reise unter den Füssen weggezogen wurde. Wer eine Reise hat, sieht diesen
 * Schirm nie - der Normalfall darf keinen Klick kosten.
 *
 * Die Liste sieht aus wie die anderen Listen der App (features/checklist,
 * features/stages): eine Zeile je Eintrag, die Rolle als Marker daneben, die
 * Knöpfe rechts.
 */
export function TripPicker() {
  const { phase, trips, meldung, fehler, oeffne, anlegen, neuLaden, istAltbestand } = useTrip()

  const [name, setName] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [anlegeFehler, setAnlegeFehler] = useState<string | null>(null)

  if (phase === 'laedt') {
    return (
      <div className="mittig">
        <Loader2 size={20} className="dreht" />
        <p>Reisen werden geladen …</p>
      </div>
    )
  }

  async function anlegenLassen(mitBeispiel: boolean) {
    const sauber = name.trim()
    if (!sauber || laeuft) return
    setLaeuft(true)
    setAnlegeFehler(null)
    try {
      await anlegen(sauber, mitBeispiel)
      setName('')
    } catch (err) {
      console.error('Reise anlegen fehlgeschlagen', err)
      setAnlegeFehler(fehlerSatz(err))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <section className="seite reiseauswahl">
      <div className="block">
        <h4>
          <Luggage size={14} /> Reisen
        </h4>

        {meldung && <p className="hinweis">{meldung}</p>}

        {trips.length === 0 && (
          <p className="leer">
            Noch keine Reise. Leg eine an — Etappen, Tagebuch, Ausgaben und Fotos hängen daran, und
            teilen lässt sie sich später über die Mitgliederliste.
          </p>
        )}

        {trips.length > 0 && (
          <ul className="eintragsliste">
            {trips.map((trip) => (
              <li key={trip.id} className="eintrag reisezeile">
                <span>
                  <b>{trip.name}</b>
                  <span className="marker">{rolleName(trip.role)}</span>
                </span>
                <div className="eintrag-knoepfe">
                  <button type="button" className="knopf" onClick={() => oeffne(trip.id)}>
                    Öffnen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {fehler && (
          <p className="hinweis">
            {fehler} Was auf diesem Gerät liegt, bleibt liegen.{' '}
            <button type="button" className="knopf still" onClick={() => void neuLaden()}>
              <RefreshCw size={13} /> Erneut versuchen
            </button>
          </p>
        )}
      </div>

      <div className="block">
        <h4>
          <Plus size={14} /> Neue Reise
        </h4>
        <div className="raster-2">
          <Field label="Name">
            <input
              value={name}
              autoComplete="off"
              placeholder="z. B. Atlantik 2027"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void anlegenLassen(false)
              }}
            />
          </Field>
        </div>
        {/*
          Zwei Knöpfe und kein Häkchen: es sind zwei verschiedene Dinge, die
          man tut, und der linke ist der Normalfall. Die Eingabetaste im
          Namensfeld löst ausdrücklich den linken aus - wer sie drückt, will
          seine Reise anlegen und keine fremde geschenkt bekommen.
        */}
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            disabled={laeuft || name.trim() === ''}
            onClick={() => void anlegenLassen(false)}
          >
            {laeuft ? <Loader2 size={14} className="dreht" /> : <Plus size={14} />} Leere Reise
            anlegen
          </button>
          <button
            type="button"
            className="knopf still"
            disabled={laeuft || name.trim() === ''}
            onClick={() => void anlegenLassen(true)}
          >
            <BookOpen size={14} /> Mit Beispieldaten
          </button>
        </div>
        <p className="abschluss">
          Eine neue Reise ist leer: keine Etappen, keine Checkliste, kein Budget. Wer sich erst
          ansehen will, wie ein ausgearbeitetes Roadbook aussieht, legt eine Reise mit Beispieldaten
          an — die vorgeplante Route Schweiz–Balkan–Schwarzes Meer, mit Etappen, Übernachtungen,
          Vignetten und Ersatzteilkiste. Ändern und löschen lässt sich darin alles.
        </p>
        {istAltbestand && trips.length === 0 && (
          <p className="abschluss">
            Auf diesem Gerät liegt bereits ein Bestand aus der Zeit vor den geteilten Reisen. Er
            gehört dann zu dieser neuen Reise und wird beim nächsten Abgleich hochgeladen.
          </p>
        )}
        {anlegeFehler && <p className="hinweis">{anlegeFehler}</p>}
      </div>
    </section>
  )
}
